import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { workspaceUsersQuery } from '@/entities/project-member';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import type { FilterOptionLabels } from './filtering';

/**
 * Names for the filter values a column type cannot label by itself: the three
 * date buckets, and the people behind a `people` column's stored user ids.
 *
 * The directory query is the same one a people cell runs, so once such a cell
 * has rendered TanStack serves both from one request.
 */
export function useFilterOptionLabels(): FilterOptionLabels {
  const { t } = useTranslation('trackers');
  const workspaceId = useCurrentWorkspaceId();

  const usersQuery = useQuery({
    ...workspaceUsersQuery(workspaceId),
    enabled: Boolean(workspaceId),
  });

  const namesById = useMemo(
    () => new Map((usersQuery.data?.data ?? []).map((u) => [u.id, u.name])),
    [usersQuery.data],
  );

  return useMemo(
    () => ({
      dateBuckets: {
        overdue: t('trackersBoard.trackersFilterPanel.overdue'),
        'this-month': t('trackersBoard.trackersFilterPanel.thisMonth'),
        later: t('trackersBoard.trackersFilterPanel.later'),
      },
      personLabel: (id: string) => namesById.get(id),
    }),
    [t, namesById],
  );
}
